import { Dialog, DialogContent, DialogTitle } from "@mzc-pdc/ui";
import { styled } from "@mui/material";
import { Children, cloneElement, useCallback, useMemo, useRef, useState } from "react";
import theme from "@styles/theme";

/**
 * 다이얼로그 본문의 좌우 여백을 spacing 단위로. 기본 테마 12px, 좁은 테마 10.5px.
 *
 * 실효 여백은 이 패딩에 스크롤바 예약 폭(클래식 11~13px, 오버레이 0px)이 더해진 값이다.
 * 24 로 두면 클래식에서 35 가 되기 때문에 절반으로 줄였다. (DAM-741)
 */
const DIALOG_CONTENT_INLINE_PADDING = 1.5;

/**
 * PDC 는 모든 variant 의 `.MuiDialogContent-root` 에 `scrollbar-gutter: stable` 을 주어 **우측에만**
 * 스크롤바 자리를 예약한다(`expandable` 은 `paddingRight: 18px` 도 함께). 예약 폭이 환경마다 달라
 * 고정 패딩으로는 맞출 수 없으므로, 좌우 양쪽에 같은 폭을 예약해(`both-edges`) 실측 없이 대칭을 만든다.
 *
 * 특이도: PDC 가 `.css-x .MuiDialogContent-root` = (0,2,0)으로 주기 때문에 `&`(0,2,0)는 이기는 것이
 * 아니라 동점이고 승패가 emotion 삽입 순서에 달린다. `&&` = (0,3,0)은 순서와 무관하게 이긴다.
 *
 * variant 로 제한하지 않는다 — 제목 고정은 스크롤 감지가 필요해 `expandable` 전용이지만, 좌우 대칭은
 * 스크롤 감지와 무관한 성질이다. 실제로 호출부 중 `file-select-modal-v2` 는 variant 를 주지 않는다.
 */
const StyledDialog = styled(Dialog)(({ theme: muiTheme }) => ({
    "&& .MuiDialogContent-root": {
        scrollbarGutter: "stable both-edges",
        paddingLeft: muiTheme.spacing(DIALOG_CONTENT_INLINE_PADDING),
        paddingRight: muiTheme.spacing(DIALOG_CONTENT_INLINE_PADDING),
    },
}));

const StickyHeaderDialog = ({ children, ...props }) => {
    const [isSticky, setIsSticky] = useState(false);
    const internalRef = useRef(null);
    const listenerAttachedRef = useRef(false); // 중복 등록 방지

    const checkSticky = useCallback(() => {
        if (!internalRef.current) return;

        const contentTop = internalRef.current.getBoundingClientRect().top;
        const firstChildTop = internalRef.current.children[0]?.getBoundingClientRect().top;

        if (contentTop - firstChildTop <= 0) {
            setIsSticky(false);
        } else {
            setIsSticky(true);
        }
    }, []);

    const mergeWithInternalRef = useCallback(
        (originalRef) => {
            return (element) => {
                if (internalRef.current && listenerAttachedRef.current) {
                    internalRef.current.removeEventListener("scroll", checkSticky);
                    listenerAttachedRef.current = false;
                }

                internalRef.current = element;

                if (element && ["expandable"].includes(props?.variant)) {
                    element.addEventListener("scroll", checkSticky);
                    listenerAttachedRef.current = true;
                }

                if (originalRef) {
                    if (typeof originalRef === "function") {
                        originalRef(element);
                    } else {
                        originalRef.current = element;
                    }
                }
            };
        },
        [checkSticky, props?.variant],
    );

    const stickyTitleStyle = {
        zIndex: 1,
        boxShadow: `0 1px 0 ${theme.palette.greySecondary[100]}`,
    };

    const childrenWithRef = useMemo(
        () =>
            Children.map(children, (child) => {
                if (!child) return null;

                if (["expandable"].includes(props?.variant)) {
                    if (child.type === DialogContent) {
                        return cloneElement(child, {
                            ...child.props,
                            ref: mergeWithInternalRef(child.ref),
                        });
                    }
                }

                return child;
            }),
        [children, props?.variant, mergeWithInternalRef],
    );

    const newEnhancedChildren = useMemo(
        () =>
            Children.map(childrenWithRef, (child) => {
                if (!child) return null;

                if (["expandable"].includes(props?.variant)) {
                    if (child.type === DialogTitle && isSticky) {
                        return cloneElement(child, {
                            ...child.props,
                            sx: {
                                ...stickyTitleStyle,
                                ...(child.props.sx || {}),
                            },
                        });
                    }
                }

                return child;
            }),
        [childrenWithRef, isSticky, props?.variant],
    );

    return <StyledDialog {...props}>{newEnhancedChildren}</StyledDialog>;
};

export default StickyHeaderDialog;
